import { useState, useCallback, useRef, useEffect } from 'react';
import ttsService, { TTSQueueItem, TTSOptions, TTSVoice } from '../lib/services/ttsService';

export interface UseTTSOptions {
  voice?: string;
  autoPlay?: boolean;
  onStart?: (item: TTSQueueItem) => void;
  onEnd?: (item: TTSQueueItem) => void;
  onError?: (item: TTSQueueItem, error: string) => void;
}

export interface UseTTSReturn {
  // 播放相关
  speak: (sessionId: string, text: string, options?: TTSOptions) => Promise<string>;
  playOnce: (text: string, options?: TTSOptions) => Promise<void>;
  stopSession: (sessionId: string) => void;
  stopAll: () => void;
  
  // 状态
  isPlaying: boolean;
  isSynthesizing: boolean;
  currentText: string;
  queueStatus: {
    total: number;
    playing: number;
    completed: number;
    pending: number;
  };
  
  // 语音配置
  voices: TTSVoice[];
  currentVoice: string;
  setVoice: (voice: string) => void;
  
  // 错误处理
  error: string | null;
  clearError: () => void;
}

export const useTTS = (options: UseTTSOptions = {}): UseTTSReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [queueStatus, setQueueStatus] = useState({
    total: 0,
    playing: 0,
    completed: 0,
    pending: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [currentVoice, setCurrentVoice] = useState(
    options.voice || 'zh_female_shuangkuaisisi_moon_bigtts'
  );
  
  const currentSessionRef = useRef<string | null>(null);
  const statusUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 获取可用语音列表
  const voices = ttsService.getVoices();

  // 更新队列状态
  const updateQueueStatus = useCallback((sessionId?: string) => {
    const status = ttsService.getQueueStatus(sessionId);
    setQueueStatus(status);
    setIsPlaying(status.playing > 0);
  }, []);

  // 开始状态更新定时器
  const startStatusUpdates = useCallback(() => {
    if (statusUpdateIntervalRef.current) {
      clearInterval(statusUpdateIntervalRef.current);
    }
    
    statusUpdateIntervalRef.current = setInterval(() => {
      updateQueueStatus(currentSessionRef.current || undefined);
    }, 500);
  }, [updateQueueStatus]);

  // 停止状态更新定时器
  const stopStatusUpdates = useCallback(() => {
    if (statusUpdateIntervalRef.current) {
      clearInterval(statusUpdateIntervalRef.current);
      statusUpdateIntervalRef.current = null;
    }
  }, []);

  // 播放文本
  const speak = useCallback(async (
    sessionId: string, 
    text: string, 
    speakOptions: TTSOptions = {}
  ): Promise<string> => {
    try {
      setError(null);
      currentSessionRef.current = sessionId;
      
      console.log(`TTS播放请求: 会话=${sessionId}, 文本="${text}"`);
      
      const ttsOptions: TTSOptions = {
        voice: speakOptions.voice || currentVoice,
        onStart: (item) => {
          console.log(`TTS开始播放: ${item.text}`);
          setCurrentText(item.text);
          setIsPlaying(true);
          updateQueueStatus(sessionId);
          if (options.onStart || speakOptions.onStart) {
            (options.onStart || speakOptions.onStart)?.(item);
          }
        },
        onEnd: (item) => {
          console.log(`TTS播放完成: ${item.text}`);
          updateQueueStatus(sessionId);
          if (options.onEnd || speakOptions.onEnd) {
            (options.onEnd || speakOptions.onEnd)?.(item);
          }
        },
        onError: (item, errorMsg) => {
          console.error(`TTS播放错误: ${item.text}`, errorMsg);
          setError(errorMsg);
          updateQueueStatus(sessionId);
          if (options.onError || speakOptions.onError) {
            (options.onError || speakOptions.onError)?.(item, errorMsg);
          }
        },
        onQueueEmpty: (completedSessionId) => {
          console.log(`TTS队列清空: 会话=${completedSessionId}`);
          if (completedSessionId === sessionId) {
            setIsPlaying(false);
            setCurrentText('');
            updateQueueStatus(sessionId);
            stopStatusUpdates();
          }
        }
      };

      const queueId = await ttsService.addToQueue(sessionId, text, ttsOptions);
      
      // 开始状态更新
      startStatusUpdates();
      
      return queueId;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '语音播放失败';
      console.error('TTS播放失败:', errorMsg);
      setError(errorMsg);
      throw err;
    }
  }, [currentVoice, options, updateQueueStatus, startStatusUpdates, stopStatusUpdates]);

  // 停止会话播放
  const stopSession = useCallback((sessionId: string) => {
    console.log(`停止TTS会话: ${sessionId}`);
    ttsService.stopSession(sessionId);
    
    if (currentSessionRef.current === sessionId) {
      setIsPlaying(false);
      setCurrentText('');
      currentSessionRef.current = null;
      stopStatusUpdates();
    }
    
    updateQueueStatus();
  }, [updateQueueStatus, stopStatusUpdates]);

  // 停止所有播放
  const stopAll = useCallback(() => {
    console.log('停止所有TTS播放');
    ttsService.stopAll();
    setIsPlaying(false);
    setIsSynthesizing(false);
    setCurrentText('');
    setQueueStatus({ total: 0, playing: 0, completed: 0, pending: 0 });
    currentSessionRef.current = null;
    stopStatusUpdates();
  }, [stopStatusUpdates]);

  // 设置语音
  const setVoice = useCallback((voice: string) => {
    console.log(`切换TTS语音: ${voice}`);
    setCurrentVoice(voice);
  }, []);

  // 单次播放文本（每次重新生成）
  const playOnce = useCallback(async (
    text: string,
    speakOptions: TTSOptions = {}
  ): Promise<void> => {
    try {
      setError(null);
      
      console.log(`TTS单次播放请求: 文本="${text}"`);
      
      const ttsOptions: TTSOptions = {
        voice: speakOptions.voice || currentVoice,
        onSynthesizing: (item) => {
          setIsSynthesizing(true);
          setCurrentText(item.text);
          if (speakOptions.onSynthesizing) {
            speakOptions.onSynthesizing(item);
          }
        },
        onSynthesizeComplete: (item) => {
          setIsSynthesizing(false);
          if (speakOptions.onSynthesizeComplete) {
            speakOptions.onSynthesizeComplete(item);
          }
        },
        onStart: (item) => {
          console.log(`DEBUG: useTTS onStart触发: ${item.text}`);
          setCurrentText(item.text);
          setIsPlaying(true);
          if (speakOptions.onStart) {
            speakOptions.onStart(item);
          }
          if (options.onStart) {
            options.onStart(item);
          }
        },
        onEnd: (item) => {
          console.log(`DEBUG: useTTS onEnd触发: ${item.text}`);
          setIsPlaying(false);
          setIsSynthesizing(false);
          setCurrentText('');
          if (speakOptions.onEnd) {
            console.log('DEBUG: useTTS 调用speakOptions.onEnd回调');
            speakOptions.onEnd(item);
          }
          if (options.onEnd) {
            console.log('DEBUG: useTTS 调用options.onEnd回调');
            options.onEnd(item);
          }
        },
        onError: (item, errorMsg) => {
          console.error(`DEBUG: useTTS onError触发: ${item.text}`, errorMsg);
          setError(errorMsg);
          setIsPlaying(false);
          setIsSynthesizing(false);
          setCurrentText('');
          if (speakOptions.onError) {
            speakOptions.onError(item, errorMsg);
          }
          if (options.onError) {
            console.log('DEBUG: useTTS 调用options.onError回调');
            options.onError(item, errorMsg);
          }
        }
      };

      await ttsService.playOnce(text, ttsOptions);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '单次语音播放失败';
      console.error('TTS单次播放失败:', errorMsg);
      setError(errorMsg);
      setIsPlaying(false);
      setCurrentText('');
      throw err;
    }
  }, [currentVoice, options]);

  // 清除错误
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      stopStatusUpdates();
      if (currentSessionRef.current) {
        ttsService.stopSession(currentSessionRef.current);
      }
    };
  }, [stopStatusUpdates]);

  // 初始化时更新一次状态
  useEffect(() => {
    updateQueueStatus();
  }, [updateQueueStatus]);

  return {
    // 播放相关
    speak,
    playOnce,
    stopSession,
    stopAll,
    
    // 状态
    isPlaying,
    isSynthesizing,
    currentText,
    queueStatus,
    
    // 语音配置
    voices,
    currentVoice,
    setVoice,
    
    // 错误处理
    error,
    clearError
  };
};

export default useTTS;